import PropTypes from 'prop-types';
import * as React from 'react';
import { useContext } from 'react';
import {
  useDropdownMenu,
} from '@restart/ui/DropdownMenu';
import useIsomorphicEffect from '@restart/hooks/useIsomorphicEffect';
import useMergedRefs from '@restart/hooks/useMergedRefs';

import MetaDropdownContext from '@components/MetaDropdown/Group/MetaDropdownContext';
import { classNames } from '@utils/classNames';
import NavbarContext from '@components/MetaDropdown/Group/NavbarContext';


const propTypes = {
  show: PropTypes.bool,
  renderOnMount: PropTypes.bool,
  flip: PropTypes.bool,
  align: PropTypes.string,
  rootCloseEvent: PropTypes.oneOf(['click', 'mousedown']),
  as: PropTypes.elementType,
  popperConfig: PropTypes.object,
};

const defaultProps = {
  flip: true,
};

export function getMetaDropdownMenuPlacement(
  alignEnd,
  dropDirection,
  isRTL,
) {
  const topStart = isRTL ? 'top-end' : 'top-start';
  const topEnd = isRTL ? 'top-start' : 'top-end';
  const bottomStart = isRTL ? 'bottom-end' : 'bottom-start';
  const bottomEnd = isRTL ? 'bottom-start' : 'bottom-end';
  const leftStart = isRTL ? 'right-start' : 'left-start';
  const leftEnd = isRTL ? 'right-end' : 'left-end';
  const rightStart = isRTL ? 'left-start' : 'right-start';
  const rightEnd = isRTL ? 'left-end' : 'right-end';

  let placement = alignEnd ? bottomEnd : bottomStart;
  if (dropDirection === 'up') placement = alignEnd ? topEnd : topStart;
  else if (dropDirection === 'end')
    placement = alignEnd ? rightEnd : rightStart;
  else if (dropDirection === 'start')
    placement = alignEnd ? leftEnd : leftStart;
  return placement;
}

const DropdownMenu =
  React.forwardRef(
    (
      {
        className,
        align,
        rootCloseEvent,
        flip,
        show: showProps,
        renderOnMount,
        as: Component = 'div',
        popperConfig,
        ...props
      },
      ref,
    ) => {

      let alignEnd = false;
      const isNavbar = useContext(NavbarContext);
      const prefix = 'dropdown-menu';
      const { align: contextAlign, drop, isRTL, theme, inverted } = useContext(MetaDropdownContext);
      align = align || contextAlign;

      const alignClasses = [];
      if (align) {
        if (typeof align === 'object') {
          const keys = Object.keys(align);

          if (keys.length) {
            const brkPoint = keys[0];
            const direction = align[brkPoint];
            alignEnd = direction === 'start';
            alignClasses.push(`${prefix}-${brkPoint}-${direction}`);
          }
        } else if (align === 'end') {
          alignEnd = true;
        }
      }

      const placement = getMetaDropdownMenuPlacement(alignEnd, drop, isRTL);

      const [menuProps, { hasShown, popper, show, toggle }] = useDropdownMenu({
        flip,
        rootCloseEvent,
        show: showProps,
        usePopper: !isNavbar && alignClasses.length === 0,
        offset: [0, 2],
        popperConfig,
        placement,
      });

      menuProps.ref = useMergedRefs(
        ref,
        menuProps.ref,
      );

      useIsomorphicEffect(() => {
        if (show) popper?.update();
      }, [show]);

      if (!hasShown && !renderOnMount) return null;

      if (typeof Component !== 'string') {
        menuProps.show = show;
        menuProps.close = () => toggle?.(false);
        menuProps.align = align;
      }

      let style = props.style;
      if (popper?.placement) {
        style = { ...props.style, ...menuProps.style };
        props['x-placement'] = popper.placement;
      }

      let background = '';
      switch (theme) {
        case "blue-bg":
          background = inverted ? "bg-white" : "bg-dblue"
          break
        case "white-bg":
          background = inverted ? "bg-dblue" : "bg-white"
          break
        case "gray-bg":
          background = inverted ? "bg-dblue" : "bg-white"
          break
        default:
      }

      return (
        <Component
          {...props}
          {...menuProps}
          style={style}
          {...((alignClasses.length || isNavbar) && {
            "data-meta-popper": "static",
          })}
          className={classNames(
            className,
            prefix,
            "absolute max-h-[208px] overflow-y-auto min-w-min py-5 m-0 list-none bg-clip-padding shadow-md border-0 z-[1000]",
            background,
            show ? "block" : "hidden",
            alignEnd && `${prefix}-end`,
            ...alignClasses
          )}
        />
      )
    },
  );

DropdownMenu.displayName = 'DropdownMenu';
DropdownMenu.propTypes = propTypes;
DropdownMenu.defaultProps = defaultProps;

export default DropdownMenu;